import { createAsyncThunk } from "@reduxjs/toolkit";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import * as productAPI from "../../../api/products";
import { IConcreteBundle } from "../../../models/catalogue";
import { IProductAPIURL, isoProductAPIURL } from "../../../models/nominals";

/**
 * Parameters for loadConcreteBundles thunk.
 */
interface LoadConcreteBundlesParams {
    productURL: IProductAPIURL;
}

/**
 * Load concrete bundles for a product.
 *
 * Fetches the concrete bundles (specific upgrade/add-on options) available
 * for a given product. These bundles define the actual products that can be
 * added as upgrades or add-ons during checkout or configuration.
 *
 * The product URL is converted to a relative path to avoid CORS issues when
 * on the admin site and the API returns a URL to the public site. This approach
 * maintains high cache density by not including site domain in the cache key.
 *
 * @param {LoadConcreteBundlesParams} params - The bundle loading parameters
 * @param {IProductAPIURL} params.productURL - The product URL to load bundles for
 *
 * @returns {Promise<IConcreteBundle[]>} Promise resolving to array of concrete bundles
 *
 * @example
 * // Load bundles for a product variant
 * dispatch(loadConcreteBundles({ productURL: product.url }));
 *
 * @example
 * // Load bundles and handle result
 * const bundles = await dispatch(loadConcreteBundles({ productURL })).unwrap();
 * console.log("Available upgrades:", bundles.filter(b => b.bundle_type === 'upgrade'));
 *
 * @sideEffects
 * - Updates bundles in `data.basket.lines[].bundles` via extraReducers
 * - Each bundle is injected into the appropriate basket line by matching triggering_product
 */
export const loadConcreteBundles = createAsyncThunk(
    "checkout/loadConcreteBundles",
    async (params: LoadConcreteBundlesParams): Promise<IConcreteBundle[]> => {
        const { productURL } = params;

        // Make sure we're dealing with a relative URL, not a FQDN. Relative URLs will
        // default to the current page domain, thereby avoiding CORS issues when on the
        // admin site and the API returns a URL to the public site. We choose to fix this
        // CORS issue here instead of by adding site domain to the product API cache key
        // since that would significantly decrease the cache density.
        const window = getWindow();
        const baseURL = window?.location.origin ?? "http://localhost";
        const parsedProductURL = new URL(
            isoProductAPIURL.unwrap(productURL),
            baseURL,
        );
        const bundles = await productAPI.listConcreteBundles(
            isoProductAPIURL.wrap(parsedProductURL.pathname),
        );

        return bundles;
    },
);
